import { useState, useEffect } from 'react'
import Modal from './Modal'
import Boton from './Boton'
import BuscadorCliente from './BuscadorCliente'
import ModalListaClientes from './ModalListaClientes'
import { cuentasWebApi } from '../api/cuentasWeb'
import { etiquetaCliente } from '../utils/cliente'
import { colors, text, space, radius, sx } from '../design/tokens'

const mensajeError = (err, fallback) => {
  const errs = err?.response?.data?.errors
  return errs ? Object.values(errs).flat().join(' ') : (err?.response?.data?.message || fallback)
}

/**
 * Vincular una cuenta de ligier.com.ar a su ficha de LGR, o pasarla a otra
 * (Martin, 9 oct 2026, caso luli_zuvic: la tienda le había creado una ficha
 * nueva y no había cómo pasarla a la suya). Arriba, las fichas con el mismo
 * email; abajo, cualquier cliente (F6 = búsqueda avanzada). Una ficha con
 * otro email pide confirmación: la persona va a ver sus compras en Mi cuenta.
 * Con la ficha puesta, las razones sociales con las que factura (las mismas
 * que le ofrece el checkout).
 */
export default function ModalVincularCuentaWeb({ cuenta, onClose, onSaved }) {
  const [ficha, setFicha] = useState(cuenta.cliente)
  const [elegido, setElegido] = useState(null)
  const [confirmar, setConfirmar] = useState('')   // mensaje del backend cuando el email es otro
  const [razones, setRazones] = useState([])
  const [empresa, setEmpresa] = useState(null)
  const [avanzada, setAvanzada] = useState(null)   // 'ficha' | 'empresa'
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const h = (e) => { if (e.key === 'F6') { e.preventDefault(); setAvanzada(ficha ? 'empresa' : 'ficha') } }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [ficha])

  useEffect(() => {
    if (!ficha) return
    cuentasWebApi.razonesSociales(cuenta.id).then(({ data }) => setRazones(data.data)).catch(() => {})
  }, [cuenta.id, ficha])

  const elegir = (c) => { setElegido(c); setConfirmar(''); setError('') }

  const vincular = async (confirmado = false) => {
    if (!elegido) return
    setGuardando(true); setError('')
    try {
      const { data } = await cuentasWebApi.vincular(cuenta.id, elegido.id, confirmado)
      setFicha(data.cliente); setElegido(null); setConfirmar('')
      onSaved()
    } catch (err) {
      if (err?.response?.data?.requiere_confirmacion) setConfirmar(err.response.data.message)
      else setError(mensajeError(err, 'No se pudo vincular.'))
    } finally { setGuardando(false) }
  }

  const agregarEmpresa = async () => {
    if (!empresa) return
    setGuardando(true); setError('')
    try {
      const { data } = await cuentasWebApi.agregarRazonSocial(cuenta.id, empresa.id)
      setRazones(data.data); setEmpresa(null)
    } catch (err) {
      setError(mensajeError(err, 'No se pudo agregar la razón social.'))
    } finally { setGuardando(false) }
  }

  const quitarEmpresa = async (id) => {
    setError('')
    try {
      const { data } = await cuentasWebApi.quitarRazonSocial(cuenta.id, id)
      setRazones(data.data)
    } catch (err) { setError(mensajeError(err, 'No se pudo quitar.')) }
  }

  const candidatos = (cuenta.candidatos || []).filter(k => k.id !== ficha?.id)

  return (
    <Modal title="Vincular cuenta web a ficha" onClose={onClose} maxWidth="600px">
      <div style={st.col}>
        <div style={st.hoy}>
          <div><span style={st.rotulo}>Cuenta</span> <strong style={{ color: colors.text }}>{cuenta.email}</strong>{cuenta.nombre && ` · ${cuenta.nombre}`}</div>
          <div><span style={st.rotulo}>Ficha</span> {ficha
            ? <><strong style={{ color: colors.text }}>{ficha.nombre}</strong> · <span style={sx.mono}>{ficha.codigo_presea || 'pendiente'}</span>{!ficha.activo && ' · inactiva'}</>
            : <span style={{ color: colors.warn }}>sin ficha</span>}
          </div>
        </div>

        <section style={st.col}>
          <div style={st.titulo}>{ficha ? 'Pasar a otra ficha' : 'Elegir la ficha'}</div>
          {candidatos.length > 0 && (
            <div style={st.chips}>
              {candidatos.map(k => (
                <button key={k.id} type="button" onClick={() => elegir(k)}
                  style={{ ...st.chip, ...(elegido?.id === k.id ? st.chipOn : {}) }}>
                  {k.nombre} <span style={sx.mono}>{k.codigo_presea || 'pendiente'}</span>{!k.activo && ' · inactiva'}
                </button>
              ))}
              <span style={st.nota}>mismo email</span>
            </div>
          )}
          <div style={st.fila}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <BuscadorCliente key={elegido?.id || 'sin'} value={elegido} onChange={elegir} />
            </div>
            <Boton variante="secondary" sm onClick={() => setAvanzada('ficha')} title="Búsqueda avanzada de cliente (F6): nombre, CUIT, DNI, localidad, email">🔍</Boton>
          </div>
          {elegido && (
            <div style={st.nota}>
              Elegida: <strong style={{ color: colors.text }}>{etiquetaCliente(elegido)}</strong> · Nº cliente <span style={sx.mono}>{elegido.codigo_presea || 'pendiente'}</span>
            </div>
          )}
          {confirmar && <div style={st.aviso}>{confirmar}</div>}
          <div style={st.botones}>
            {confirmar
              ? <Boton variante="primary" disabled={guardando} onClick={() => vincular(true)}>Vincular igual</Boton>
              : <Boton variante="primary" disabled={!elegido || guardando} onClick={() => vincular(false)}>{guardando ? 'Vinculando…' : (ficha ? 'Cambiar ficha' : 'Vincular')}</Boton>}
          </div>
        </section>

        {ficha && (
          <section style={{ ...st.col, ...st.separado }}>
            <div style={st.titulo}>Razones sociales para facturar</div>
            <div style={st.nota}>Las que la persona puede elegir en el checkout de la tienda.</div>
            {razones.length === 0 ? <div style={st.nota}>Ninguna.</div> : (
              <div style={st.col}>
                {razones.map(r => (
                  <div key={r.cliente_id} style={st.razon}>
                    <div style={{ minWidth: 0 }}>
                      <strong style={{ color: colors.text }}>{r.nombre}</strong>
                      <div style={st.nota}>CUIT <span style={sx.mono}>{r.cuit || '—'}</span> · Nº cliente <span style={sx.mono}>{r.codigo_presea || 'pendiente'}</span></div>
                    </div>
                    <Boton variante="ghost" sm onClick={() => quitarEmpresa(r.cliente_id)}>Quitar</Boton>
                  </div>
                ))}
              </div>
            )}
            <div style={st.fila}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <BuscadorCliente key={empresa?.id || 'sin-empresa'} value={empresa} onChange={setEmpresa} placeholder="Agregar razón social: nombre o CUIT..." />
              </div>
              <Boton variante="secondary" sm onClick={() => setAvanzada('empresa')} title="Búsqueda avanzada de cliente (F6)">🔍</Boton>
              <Boton variante="secondary" sm disabled={!empresa || guardando} onClick={agregarEmpresa}>Agregar</Boton>
            </div>
          </section>
        )}

        {error && <div style={st.error}>{error}</div>}
        <div style={st.botones}>
          <Boton variante="ghost" onClick={onClose}>Cerrar</Boton>
        </div>
      </div>
      {avanzada && (
        <ModalListaClientes
          onSelect={(c) => { if (avanzada === 'empresa') setEmpresa(c); else elegir(c); setAvanzada(null) }}
          onClose={() => setAvanzada(null)}
        />
      )}
    </Modal>
  )
}

const st = {
  col: { display: 'flex', flexDirection: 'column', gap: space.sm },
  hoy: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: text.body, color: colors.textSec },
  rotulo: { display: 'inline-block', minWidth: 52, fontSize: text.meta, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: colors.textMeta },
  titulo: { fontSize: text.body, fontWeight: 600, color: colors.text },
  separado: { borderTop: `1px solid ${colors.borderSoft}`, paddingTop: space.lg },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' },
  chip: { padding: '4px 10px', fontSize: text.sec, border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: colors.bg, color: colors.text, cursor: 'pointer' },
  chipOn: { borderColor: colors.link, background: colors.linkBg },
  fila: { display: 'flex', gap: space.sm, alignItems: 'flex-start' },
  nota: { fontSize: text.sec, color: colors.textMeta },
  razon: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space.sm, padding: '6px 10px', border: `1px solid ${colors.borderSoft}`, borderRadius: radius.sm },
  aviso: { padding: '8px 12px', borderRadius: radius.md, background: colors.warnBg, color: colors.warn, fontSize: text.body },
  error: { padding: '8px 12px', borderRadius: radius.md, background: colors.errBg, color: colors.err, fontSize: text.body },
  botones: { display: 'flex', justifyContent: 'flex-end', gap: space.sm },
}
